import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';
import { AMERICAN_COUNTRIES } from '../data/geo';

// Vitrina pública (sin login) de empresas. `tipo` = 'productos' o 'servicios':
// - productos: lista empresas con tienda activa; el botón lleva a /p/:slug/tienda
// - servicios: lista empresas con agendamiento activo; lleva a /p/:slug/cita
// Buscador por nombre/descripción + filtros de país, departamento, ciudad y barrio. Los
// filtros SOLO se aplican al presionar "Buscar" (no al cambiar cada campo).
// Paginado de 50 en 50, ordenado por orden de creación de la empresa.
const CONFIG = {
  productos: {
    title: 'Vitrina de productos',
    subtitle: 'Explora todas las empresas que venden productos en línea.',
    endpoint: '/api/vitrina/productos',
    section: 'tienda',
    button: 'Ver esta tienda',
  },
  servicios: {
    title: 'Vitrina de servicios',
    subtitle: 'Explora todas las empresas que ofrecen servicios para agendar.',
    endpoint: '/api/vitrina/servicios',
    section: 'cita',
    button: 'Ver los servicios de esta empresa',
  },
};

const emptyFilters = { q: '', pais: '', department: '', city: '', district: '', category: '' };

function Vitrina({ tipo }) {
  const cfg = CONFIG[tipo] || CONFIG.productos;
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [filters, setFilters] = useState(emptyFilters);       // lo que se escribe
  const [applied, setApplied] = useState(emptyFilters);       // lo que se aplica
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ companies: [], total: 0, totalPages: 1, page: 1, categories: [] });
  const [categories, setCategories] = useState([]);           // opciones del dropdown
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams();
    if (applied.q.trim()) params.set('q', applied.q.trim());
    if (applied.pais) params.set('pais', applied.pais);
    if (applied.department.trim()) params.set('department', applied.department.trim());
    if (applied.city.trim()) params.set('city', applied.city.trim());
    if (applied.district.trim()) params.set('district', applied.district.trim());
    if (applied.category) params.set('category', applied.category);
    params.set('page', String(page));

    setLoading(true);
    fetch(`${cfg.endpoint}?${params.toString()}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => {
        setData(d);
        // Mantiene la lista completa de categorías aunque el filtro reduzca resultados.
        if (Array.isArray(d.categories) && d.categories.length) setCategories(d.categories);
      })
      .catch(() => setData({ companies: [], total: 0, totalPages: 1, page: 1, categories: [] }))
      .finally(() => setLoading(false));
  }, [cfg.endpoint, applied, page]);

  const setField = (field, value) => setFilters(prev => ({ ...prev, [field]: value }));
  const applyFilters = () => { setApplied(filters); setPage(1); };
  const clearFilters = () => { setFilters(emptyFilters); setApplied(emptyFilters); setPage(1); };
  const hasFilters = Object.values(applied).some(v => (v || '').trim());

  const inputStyle = {
    width: '100%', padding: '9px 12px', backgroundColor: theme.colors.background,
    border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.text,
    fontSize: '0.9rem', boxSizing: 'border-box', outline: 'none',
  };
  const primaryBtn = {
    padding: '9px 22px', backgroundColor: theme.colors.primary, color: theme.colors.background,
    border: 'none', borderRadius: '7px', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem',
  };
  const ghostBtn = {
    padding: '9px 18px', background: 'none', border: `1px solid ${theme.colors.border}`,
    color: theme.colors.text, borderRadius: '7px', cursor: 'pointer', fontSize: '0.9rem',
  };
  const th = { textAlign: 'left', padding: '10px 12px', color: theme.colors.textMuted, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap', borderBottom: `1px solid ${theme.colors.border}` };
  const td = { padding: '10px 12px', color: theme.colors.text, fontSize: '0.86rem', verticalAlign: 'middle', borderBottom: `1px solid ${theme.colors.border}33` };

  return (
    <main style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '1.5rem 1rem' : '2rem 1.5rem' }}>
      {/* Encabezado con botón "Crear mi propia vitrina" arriba a la derecha */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.2rem' }}>
        <div>
          <h1 style={{ color: theme.colors.primary, fontSize: isMobile ? '1.4rem' : '1.7rem', margin: 0 }}>{cfg.title}</h1>
          <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem', margin: '0.4rem 0 0' }}>{cfg.subtitle}</p>
        </div>
        <button
          onClick={() => navigate('/register')}
          style={{ padding: '6px 14px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.primary}`, color: theme.colors.primary, borderRadius: '7px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          + Crear mi propia vitrina
        </button>
      </div>

      {/* Buscador + filtros */}
      <div style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border}`, borderRadius: '12px', padding: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ position: 'relative', marginBottom: '0.7rem' }}>
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: theme.colors.textMuted, pointerEvents: 'none' }}>🔍</span>
          <input
            value={filters.q}
            onChange={e => setField('q', e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }}
            placeholder="Buscar por nombre o descripción de la empresa..."
            style={{ ...inputStyle, paddingLeft: '34px' }}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '0.6rem', marginBottom: '0.8rem' }}>
          <select value={filters.pais} onChange={e => setField('pais', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">Todos los países</option>
            {AMERICAN_COUNTRIES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <input value={filters.department} onChange={e => setField('department', e.target.value)} onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }} placeholder="Departamento" style={inputStyle} />
          <input value={filters.city} onChange={e => setField('city', e.target.value)} onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }} placeholder="Ciudad" style={inputStyle} />
          <input value={filters.district} onChange={e => setField('district', e.target.value)} onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }} placeholder="Barrio" style={inputStyle} />
          <input
            list="vitrina-categorias"
            value={filters.category}
            onChange={e => setField('category', e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }}
            placeholder="Categoría"
            style={inputStyle}
          />
          <datalist id="vitrina-categorias">
            {categories.map(c => <option key={c} value={c} />)}
          </datalist>
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button onClick={applyFilters} style={primaryBtn}>🔍 Buscar</button>
          {hasFilters && <button onClick={clearFilters} style={ghostBtn}>Limpiar</button>}
        </div>
      </div>

      {/* Resultados */}
      {loading ? (
        <p style={{ color: theme.colors.textMuted, textAlign: 'center', padding: '3rem' }}>Cargando...</p>
      ) : data.companies.length === 0 ? (
        <p style={{ color: theme.colors.textMuted, textAlign: 'center', padding: '3rem' }}>No se encontraron empresas con esos filtros.</p>
      ) : (
        <div style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border}`, borderRadius: '12px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
            <thead>
              <tr>
                <th style={th}>Logo</th>
                <th style={th}>Empresa</th>
                <th style={th}>Categoría</th>
                <th style={th}>Descripción</th>
                <th style={th}>País</th>
                <th style={th}>Departamento</th>
                <th style={th}>Ciudad</th>
                <th style={th}>Barrio</th>
                <th style={th}>Dirección</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {data.companies.map(c => {
                const openVitrina = () => window.open(`/p/${c.slug}/${cfg.section}`, '_blank', 'noopener,noreferrer');
                return (
                <tr key={c.slug}>
                  <td style={td}>
                    <div onClick={openVitrina} title={cfg.button} style={{ cursor: 'pointer', display: 'inline-block' }}>
                      {c.logo
                        ? <img src={c.logo} alt={c.name} style={{ width: '46px', height: '46px', objectFit: 'contain', borderRadius: '8px', border: `1px solid ${theme.colors.border}` }} />
                        : <div style={{ width: '46px', height: '46px', borderRadius: '8px', backgroundColor: theme.colors.background, border: `1px solid ${theme.colors.border}` }} />}
                    </div>
                  </td>
                  <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    <span onClick={openVitrina} title={cfg.button} style={{ cursor: 'pointer', color: theme.colors.primary }}>{c.name}</span>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{c.category || '—'}</td>
                  <td style={{ ...td, ...(isMobile ? { minWidth: '320px', maxWidth: '360px' } : { minWidth: '360px', maxWidth: '460px' }), color: theme.colors.textMuted }}>{c.description ? (c.description.length > 250 ? `${c.description.slice(0, 250)}…` : c.description) : '—'}</td>
                  <td style={td}>{c.pais || '—'}</td>
                  <td style={td}>{c.department || '—'}</td>
                  <td style={td}>{c.city || '—'}</td>
                  <td style={td}>{c.district || '—'}</td>
                  <td style={{ ...td, color: theme.colors.textMuted }}>{c.address || '—'}</td>
                  <td style={{ ...td, width: '150px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <button onClick={openVitrina} style={{ ...primaryBtn, maxWidth: '150px', whiteSpace: 'normal', lineHeight: 1.25, textAlign: 'center', padding: '8px 14px', fontSize: '0.82rem' }}>{cfg.button}</button>
                      {c.location && (
                        <button
                          onClick={() => window.open(c.location, '_blank', 'noopener,noreferrer')}
                          title="Ver ubicación en Google Maps"
                          aria-label="Ver ubicación en Google Maps"
                          style={{ ...primaryBtn, padding: '9px 12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                        >
                          📍
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginado */}
      {data.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.8rem', marginTop: '1.5rem' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ ...ghostBtn, opacity: page === 1 ? 0.4 : 1, cursor: page === 1 ? 'not-allowed' : 'pointer' }}>← Anterior</button>
          <span style={{ color: theme.colors.textMuted, fontSize: '0.88rem' }}>Página {data.page} de {data.totalPages}</span>
          <button onClick={() => setPage(p => Math.min(data.totalPages, p + 1))} disabled={page >= data.totalPages} style={{ ...ghostBtn, opacity: page >= data.totalPages ? 0.4 : 1, cursor: page >= data.totalPages ? 'not-allowed' : 'pointer' }}>Siguiente →</button>
        </div>
      )}
      {data.total > 0 && (
        <p style={{ textAlign: 'center', color: theme.colors.textMuted, fontSize: '0.82rem', marginTop: '0.8rem' }}>{data.total} empresa(s) encontrada(s)</p>
      )}
    </main>
  );
}

export default Vitrina;
